import React__default, { forwardRef, useState, useRef, useImperativeHandle, useEffect, useLayoutEffect } from "react";
import { a as isEmpty } from "../../../helpers-N4qF49_T.js";
import { a as useFocus } from "../../../hooks-BlwR7OOF.js";
import { T as TYPES } from "../../../inputTypes-Cj9_-nL4.js";
import "../../../dayjs.min-BUYAusmZ.js";
import { devLog } from "../../utils/devLog.js";
import { Box } from "../Box/Box.js";
/* empty css                                */
import { COLORS } from "../../EatClubTheme.js";
import useStyles from "./TextFieldStyles.js";
import { Button } from "../Button/Button.js";
const TextField = forwardRef(
  ({
    className = "",
    style = {},
    label = "",
    placeholder = "",
    value = "",
    onChange = (newValue) => {
    },
    type = "text",
    // e.g. text, email, URL, numeric, phone
    autoCorrect = false,
    autoCapitalize = "sentences",
    autoComplete = false,
    minNumber = null,
    // The minimum number this can be when set to a number type TODO have separate validation instead
    maxNumber = null,
    // The maximum number this can be when set to a number type TODO have separate validation instead
    maxCharacters = null,
    validation = "",
    onEnter = (e) => {
    },
    onBlur = (e) => {
    },
    format = null,
    name = "",
    prefix = "",
    // Ensure this string is at the start of the value
    suffix = "",
    inputPrefix = null,
    // A prefix that shows in the input, but isn't part of the value. e.g. dollar signs
    inputSuffix = "",
    // A suffix that shows in the input, but isn't part of the value. e.g. percent signs
    disabled = false,
    // Whether this field is grayed out and uneditable
    editable = true,
    // Whether the value in this field can be changed. Overridden if disabled
    autofocus = false,
    // Whether this text field will be focused when mounted
    onFocus = (e) => {
    },
    // Callback for when this is set to focused (e.g. typing in)
    icon = null,
    // Have an icon at the start of the field
    error = null,
    // Error message. Send through whitespace to have the field show as an error without a message underneath it
    clearError = () => {
    },
    decimalsLimit = 2,
    required = false,
    id = null,
    inputId = null,
    // Used when embedded in things like pin payments that modify the field directly
    inputProps = {},
    toggleVisibility = false
    // For passwords only. If type is set to password and this is true, it lets the user toggle viewing the password
  }, ref) => {
    var _a;
    const classes = useStyles();
    const [cursorPosition, setCursorPosition] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [inputRef, setInputFocus] = useFocus();
    const inputPrefixRef = useRef(null);
    const [passwordVisible, setPasswordVisible] = useState(false);
    document.activeElement === inputRef.current;
    useImperativeHandle(ref, () => ({
      ...inputRef.current,
      addEventListener: inputRef.current.addEventListener,
      scrollIntoView: (options) => inputRef.current.scrollIntoView(options),
      scrollTo: (options) => inputRef.current.scrollTo(options),
      scroll: (options) => inputRef.current.scroll(options),
      click: () => {
        inputRef.current.click();
      },
      focus: (e) => {
        setInputFocus(true);
        inputRef.current.focus();
      }
    }));
    useEffect(() => {
      if (inputRef.current && autofocus) {
        setTimeout(() => {
          if (inputRef.current && autofocus) {
            setInputFocus();
          }
        }, 50);
      }
    }, [inputRef.current, autofocus]);
    const handleFocus = (event) => {
      setFocused(true);
      onFocus(event);
    };
    const handleBlur = (event) => {
      setFocused(false);
      onBlur(event);
      if (type === TYPES.CURRENCY) {
        const newValue = event.target.value;
        if (newValue === null || newValue === "") {
          onChange(`0.${"0".repeat(decimalsLimit)}`);
          return;
        }
        const decimalIndex = newValue.indexOf(".");
        if (decimalIndex === -1) {
          onChange(`${newValue}.${"0".repeat(decimalsLimit)}`);
          return;
        }
        const decimals = newValue.substring(decimalIndex + 1).length;
        if (decimals < decimalsLimit) {
          onChange(`${newValue}${"0".repeat(decimalsLimit - decimals)}`);
        }
      }
    };
    const handleOnChange = (event) => {
      if (!editable || disabled) {
        return;
      }
      clearError();
      const { target } = event;
      const { position } = target;
      setCursorPosition(target.selectionStart);
      let newValue = event.target.value;
      if (type === TYPES.CURRENCY) {
        const regex = /^[\d]*([\d]*|[.]?)[\d]*$/;
        if (!regex.test(newValue)) {
          return;
        }
        if (newValue.length === 1 && newValue[0] === ".") {
          newValue = `0${newValue}`;
        } else if (newValue.length > 1 && newValue[0] === "0" && newValue[1] !== ".") {
          newValue = newValue.substring(1);
        }
        const decimalIndex = newValue.indexOf(".");
        if (decimalIndex !== -1 && newValue.substring(decimalIndex + 1).length > decimalsLimit) {
          return;
        }
        onChange(newValue);
        return;
      }
      if ((newValue == null ? void 0 : newValue.length) < `${value}`.length) {
        onChange(event.target.value);
        return;
      }
      if (maxCharacters !== null && maxCharacters > 0 && newValue.length > maxCharacters) {
        console.log("hit max characters");
        return;
      }
      if (format !== null) {
        devLog("info", "formatting with function");
        newValue = format(newValue);
        onChange(newValue);
        return;
      }
      const isNumeric = minNumber !== null || maxNumber !== null;
      if (isNumeric && newValue) {
        newValue = parseFloat(newValue) || 0;
      }
      if (minNumber !== null && newValue < minNumber) {
        console.log("number too low");
        newValue = minNumber;
      }
      if (maxNumber !== null && newValue > maxNumber) {
        console.log("number too high");
        newValue = maxNumber;
      }
      if (validation === "numbers_only") {
        const regex = /^-?[\d]*([\d]*|[.]?)[\d]*$/;
        if (!regex.test(newValue)) {
          console.log("invalid character");
          return;
        }
      }
      onChange(newValue);
    };
    const getAutoCorrect = () => {
      if (type === TYPES.TEXT && autoCorrect === true) {
        return "on";
      }
      return "off";
    };
    const handleKeyPress = (event) => {
      if (document.activeElement !== inputRef.current) {
        return;
      }
      if (event.key === "Enter") {
        onEnter(event);
      }
      if (event.key === "Escape") {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        handleBlur(event);
      }
    };
    useEffect(() => {
      document.addEventListener("keydown", handleKeyPress);
      return () => document.removeEventListener("keydown", handleKeyPress);
    });
    const [leftPadding, setLeftPadding] = useState(14);
    const calculateLeftPadding = (newInputPrefix, newIcon) => {
      var _a2;
      let padding = 0;
      if (newInputPrefix) {
        padding += 10 + (((_a2 = inputPrefixRef == null ? void 0 : inputPrefixRef.current) == null ? void 0 : _a2.clientWidth) || 0) + 10;
      } else {
        padding = 15;
      }
      if (newIcon) {
        padding += 20;
      }
      setLeftPadding(padding);
    };
    useLayoutEffect(() => {
      calculateLeftPadding(inputPrefix, icon);
    }, [inputPrefix, icon]);
    const styles = {
      wrapper: {
        // ...style,
        ...style == null ? void 0 : style.wrapper
      },
      // container: {
      //   ...style,
      //   // ...style?.container,
      //   ...style?.wrapper,
      // },
      innerContainer: {
        ...style == null ? void 0 : style.innerContainer
      },
      label: {
        marginBottom: "10px",
        ...style == null ? void 0 : style.label
      },
      textField: {
        ...style == null ? void 0 : style.textField
      },
      textFieldInner: {
        position: "relative",
        ...style == null ? void 0 : style.textFieldInner
      },
      disabled: {
        backgroundColor: `${COLORS.EC_GRAY} !important`,
        ...style == null ? void 0 : style.disabled
      },
      placeholder: {
        ...style == null ? void 0 : style.placeholder
      },
      input: {
        padding: "11px 15px",
        boxSizing: "border-box",
        width: "100%",
        fontSize: "16px",
        minWidth: "100px",
        borderRadius: "4px",
        border: `1px solid ${COLORS.NIMBUS}`,
        cursor: "text",
        ...style == null ? void 0 : style.input,
        paddingLeft: ((_a = style == null ? void 0 : style.input) == null ? void 0 : _a.paddingLeft) ?? `${leftPadding}px`
        // Allow custom left padding
      },
      inputHover: {
        border: "1px solid #212121",
        ...style == null ? void 0 : style.inputHover
      },
      inputActive: {
        ...style == null ? void 0 : style.inputActive
      },
      inputErrors: {
        border: `1px solid ${COLORS.RED}`,
        ...style == null ? void 0 : style.inputErrors
      },
      errorText: {
        color: COLORS.RED,
        fontSize: "12px",
        margin: !isEmpty(`${error}`.trim()) ? "3px 15px 0 15px" : "0 15px",
        ...style == null ? void 0 : style.errorText
      },
      inputPrefix: {
        fontSize: "16px",
        position: "absolute",
        // marginTop: "10px",
        marginLeft: "10px",
        // top: '50%',
        // transform: 'translateY(-50%)',
        display: "flex",
        alignItems: "center",
        height: "100%",
        ...style == null ? void 0 : style.inputPrefix
      },
      inputSuffix: {
        fontSize: "16px",
        position: "absolute",
        // marginTop: "10px",
        // marginRight: '10px',
        right: "10px",
        // top: '50%',
        // transform: 'translateY(-50%)',
        display: "flex",
        alignItems: "center",
        height: "100%",
        ...style == null ? void 0 : style.inputSuffix
      }
    };
    const getEffectiveType = () => {
      if (type === "password" && toggleVisibility && passwordVisible) {
        return "text";
      }
      return type;
    };
    return /* @__PURE__ */ React__default.createElement(Box, { id, className: `text-field-container ${className}`, style: styles.wrapper }, /* @__PURE__ */ React__default.createElement(Box, { style: styles.innerContainer }, label && /* @__PURE__ */ React__default.createElement(Box, { style: styles.label, className: "paragraph-small" }, label, required && "*"), /* @__PURE__ */ React__default.createElement(Box, { className: "text-field", style: styles.textField }, /* @__PURE__ */ React__default.createElement(
      Box,
      {
        className: `text-field-inner ${disabled ? "text-field-disabled" : ""}`,
        style: {
          ...styles.textFieldInner,
          ...disabled && styles.disabled
        },
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false)
      },
      inputPrefix && /* @__PURE__ */ React__default.createElement(
        Box,
        {
          ref: inputPrefixRef,
          onMouseDown: (e) => e.preventDefault(),
          className: "text-field-input-prefix",
          style: {
            // ...styles.input,
            ...styles.inputPrefix,
            ...(value === null || value === "") && styles.placeholder
            // ...(!disabled && editable && hovered && styles.inputHover),
            // ...(focused && styles.inputActive),
            // ...(error && styles.inputErrors),
          }
        },
        inputPrefix
      ),
      inputSuffix && /* @__PURE__ */ React__default.createElement(
        Box,
        {
          onMouseDown: (e) => e.preventDefault(),
          className: "text-field-input-suffix",
          style: {
            // ...styles.input,
            ...styles.inputSuffix,
            ...(value === null || value === "") && styles.placeholder
            // ...(!disabled && editable && hovered && styles.inputHover),
            // ...(focused && styles.inputActive),
            // ...(error && styles.inputErrors),
          }
        },
        inputSuffix
      ),
      /* @__PURE__ */ React__default.createElement(
        "input",
        {
          id: inputId,
          onBlur: handleBlur,
          autoCorrect: getAutoCorrect(),
          spellCheck: type === TYPES.TEXT && autoCorrect === true,
          autoCapitalize,
          name,
          autoComplete: autoComplete === true ? "on" : autoComplete || "one-time-code",
          className: `text-field-input ${classes.input}`,
          type: getEffectiveType(),
          value: value !== null ? value : "",
          placeholder,
          onChange: handleOnChange,
          onFocus: handleFocus,
          tabIndex: disabled ? -1 : 0,
          ref: inputRef,
          disabled: disabled || !editable,
          style: {
            ...styles.input,
            ...(value === null || value === "") && styles.placeholder,
            ...!disabled && editable && hovered && styles.inputHover,
            ...focused && styles.inputActive,
            ...error && styles.inputErrors
          },
          ...inputProps
        }
      ),
      type === "password" && toggleVisibility && /* @__PURE__ */ React__default.createElement(
        Button,
        {
          style: {
            position: "absolute",
            right: "15px",
            // Should match text field's padding
            top: "50%",
            transform: "translateY(-50%)",
            color: COLORS.SMOKE,
            button: { textDecoration: "underline" }
          },
          type: "text",
          onClick: () => setPasswordVisible(!passwordVisible)
        },
        passwordVisible ? "Hide" : "Show"
      )
    ))), error && /* @__PURE__ */ React__default.createElement(Box, { style: styles.errorText }, error));
  }
);
export {
  TextField
};
//# sourceMappingURL=TextField.js.map
